/**
 * START / FINISH tű az aktivitás térképein.
 *
 * Két formában kell ugyanaz: React-elemként a feed-kártya statikus képe fölé,
 * és nyers DOM-elemként a Mapbox `Marker`-hez (`MapView`). A kettő ugyanazt a
 * jelölést és a `routePin.css` stílusát használja, hogy egyformán nézzen ki.
 *
 * ⚠️ MINDIG a MEGJELENÍTETT nyomvonal első/utolsó pontjára kerül: idegen
 * aktivitásnál ez a privát zónánál már levágott útvonal, tehát a tű nem árulja
 * el, hol lakik a másik.
 */
import type { CSSProperties } from 'react';
import { distanceM } from '@/game/geo';
import './routePin.css';

/** `loop`: a rajt és a cél egybeesik — egy közös (lila) tű kettő helyett. */
export type RoutePinKind = 'start' | 'finish' | 'loop';

export interface RouteEndpoints {
  start: { lat: number; lng: number };
  finish: { lat: number; lng: number };
}

/**
 * Ennél közelebb a két végpont egy tűt kap: a két tű különben egymást
 * takarná. Körnél (ugyanoda érkezel vissza) ez a gyakori eset.
 */
const SAME_PLACE_M = 40;

/** A kirakandó tűk: kettő, vagy egy közös, ha a rajt és a cél egybeesik. */
export function pinsFor(endpoints: RouteEndpoints): { kind: RoutePinKind; lat: number; lng: number }[] {
  if (distanceM(endpoints.start, endpoints.finish) < SAME_PLACE_M) {
    return [{ kind: 'loop', ...endpoints.finish }];
  }
  return [
    { kind: 'start', ...endpoints.start },
    { kind: 'finish', ...endpoints.finish },
  ];
}

/** Csepp alakú tű, a hegye alul középen. */
const PIN_PATH = 'M9 0C4 0 0 4 0 9c0 6.5 9 15 9 15s9-8.5 9-15c0-5-4-9-9-9z';

/*
  CÉL = KOCKÁS ZÁSZLÓ (Geri, #86). Szélesebb dobozban (34×24), mint a csepp
  (18×24): a rúd alja alul KÖZÉPEN van, így a pont helye (anchor: bottom) nem
  változik, a lap pedig a rúdtól jobbra elfér. 4×3 kocka, a sötétek
  `currentColor`, a világosak a tű „lyuk” színe. Mérve a böngészőben: a 18
  széles dobozban a lap a cseppek mellett olvashatatlanul kicsi volt.
*/
const FLAG_VIEWBOX = '0 0 34 24';
const FLAG_POLE = { x: 16.1, y: 1, width: 1.8, height: 23 } as const;
const FLAG = { x: 17.9, y: 1.5, cols: 4, rows: 3, cell: 3.6 } as const;

/** A zászló kockái: `[x, y, sötét-e]`. */
function flagCells(): [number, number, boolean][] {
  const cells: [number, number, boolean][] = [];
  for (let row = 0; row < FLAG.rows; row += 1) {
    for (let col = 0; col < FLAG.cols; col += 1) {
      cells.push([FLAG.x + col * FLAG.cell, FLAG.y + row * FLAG.cell, (row + col) % 2 === 0]);
    }
  }
  return cells;
}

/** Az útvonal két végpontja, vagy `null`, ha nincs legalább két pont. */
export function routeEndpoints(points: readonly { lat: number; lng: number }[]): RouteEndpoints | null {
  if (points.length < 2) return null;
  const start = points[0]!;
  const finish = points[points.length - 1]!;
  return { start: { lat: start.lat, lng: start.lng }, finish: { lat: finish.lat, lng: finish.lng } };
}

export function RoutePin({
  kind,
  style,
}: {
  kind: RoutePinKind;
  /** Statikus kép fölé helyezéskor a pozíció (`left`/`top` százalékban). */
  style?: CSSProperties;
}) {
  return (
    <div className={`route-pin route-pin--${kind}${style ? ' route-pin--overlay' : ''}`} style={style} aria-hidden="true">
      <svg className="route-pin__icon" viewBox={kind === 'finish' ? FLAG_VIEWBOX : '0 0 18 24'}>
        {kind === 'finish' ? (
          <>
            <rect {...FLAG_POLE} fill="currentColor" />
            <rect
              x={FLAG.x}
              y={FLAG.y}
              width={FLAG.cols * FLAG.cell}
              height={FLAG.rows * FLAG.cell}
              fill="var(--route-pin-on)"
              stroke="currentColor"
              strokeWidth="0.6"
            />
            {flagCells().filter(([, , dark]) => dark).map(([x, y]) => (
              <rect key={`${x}-${y}`} x={x} y={y} width={FLAG.cell} height={FLAG.cell} fill="currentColor" />
            ))}
          </>
        ) : (
          <>
            <path d={PIN_PATH} fill="currentColor" />
            <circle cx="9" cy="9" r="3.5" fill="var(--route-pin-on)" />
          </>
        )}
      </svg>
    </div>
  );
}

/** Ugyanaz a tű nyers DOM-elemként, a Mapbox `Marker`-hez. */
export function createRoutePinElement(kind: RoutePinKind): HTMLElement {
  const root = document.createElement('div');
  root.className = `route-pin route-pin--${kind}`;
  root.setAttribute('aria-hidden', 'true');

  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  svg.setAttribute('class', 'route-pin__icon');
  svg.setAttribute('viewBox', kind === 'finish' ? FLAG_VIEWBOX : '0 0 18 24');
  const rect = (x: number, y: number, width: number, height: number, fill: string) => {
    const element = document.createElementNS(svgNs, 'rect');
    element.setAttribute('x', String(x));
    element.setAttribute('y', String(y));
    element.setAttribute('width', String(width));
    element.setAttribute('height', String(height));
    element.setAttribute('fill', fill);
    return element;
  };
  if (kind === 'finish') {
    const cloth = rect(FLAG.x, FLAG.y, FLAG.cols * FLAG.cell, FLAG.rows * FLAG.cell, 'var(--route-pin-on)');
    cloth.setAttribute('stroke', 'currentColor');
    cloth.setAttribute('stroke-width', '0.6');
    svg.append(
      rect(FLAG_POLE.x, FLAG_POLE.y, FLAG_POLE.width, FLAG_POLE.height, 'currentColor'),
      cloth,
      ...flagCells().filter(([, , dark]) => dark).map(([x, y]) => rect(x, y, FLAG.cell, FLAG.cell, 'currentColor')),
    );
  } else {
    const path = document.createElementNS(svgNs, 'path');
    path.setAttribute('d', PIN_PATH);
    path.setAttribute('fill', 'currentColor');
    const hole = document.createElementNS(svgNs, 'circle');
    hole.setAttribute('cx', '9');
    hole.setAttribute('cy', '9');
    hole.setAttribute('r', '3.5');
    hole.setAttribute('fill', 'var(--route-pin-on)');
    svg.append(path, hole);
  }

  root.append(svg);
  return root;
}
